// src/components/Signup/SignupStep2.jsx
import React from "react";
import {
    Box, Typography, Chip, Select, MenuItem,
    InputLabel, FormControl, Stack, Divider, TextField,
} from "@mui/material";

const MAX_SELECTED = 3;
const CUSTOM_MAX = 10;   // ✅ 추가: 기타 직접작성 최대 10자
const allTraits = [
    { label: "도미넌트", color: "error" },
    { label: "서브미시브", color: "primary" },
    { label: "마스터/미스트레스", color: "error" },
    { label: "슬레이브", color: "primary" },
    { label: "오너", color: "error" },
    { label: "펫", color: "primary" },
    { label: "대디/마미", color: "error" },
    { label: "리틀", color: "primary" },
    { label: "브랫 테이머", color: "error" },
    { label: "브랫", color: "primary" },
    { label: "사디스트", color: "error" },
    { label: "마조히스트", color: "primary" },
    { label: "헌터", color: "error" },
    { label: "프레이", color: "primary" },
    { label: "디그레이더", color: "error" },
    { label: "디그레이디", color: "primary" },
    { label: "스팽커", color: "error" },
    { label: "스팽키", color: "primary" },
    { label: "리거", color: "error" },
    { label: "로프버니", color: "primary" },
];

export default function SignupStep2({
    selectedTraits, setSelectedTraits,
    recruitMain, setRecruitMain,
    recruitSub, setRecruitSub,
    recruitNote, setRecruitNote,
    recruitFlag, setRecruitFlag,
    offlineStatus, setOfflineStatus,
    recruitCustom, setRecruitCustom,
}) {
    const toggleTrait = (trait) => {
        const exists = selectedTraits.includes(trait);
        if (exists) {
            setSelectedTraits(selectedTraits.filter((t) => t !== trait));
        } else {
            if (selectedTraits.length >= MAX_SELECTED) {
                alert("최대 3개까지만 선택할 수 있습니다.");
                return;
            }
            setSelectedTraits([...selectedTraits, trait]);
        }
    };

    const renderTraitGroup = (color, label) => {
        const group = allTraits.filter((t) => t.color === color);
        if (group.length === 0) return null;

        return (
            <Box sx={{ mt: 3 }}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: "bold",
                        color: `${color}.main`,
                        mb: 1,
                        textAlign: "left",
                    }}
                >
                    {label}
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {group.map((trait) => (
                        <Chip
                            key={trait.label}
                            label={trait.label}
                            color={selectedTraits.includes(trait.label) ? trait.color : "default"}
                            variant={selectedTraits.includes(trait.label) ? "filled" : "outlined"}
                            onClick={() => toggleTrait(trait.label)}
                        />
                    ))}
                </Box>

                <Divider sx={{ mt: 2 }} />
            </Box>
        );
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ textAlign: "left" }}>
                세부 성향 선택 (최대 3개)
            </Typography>

            {renderTraitGroup("error", "Top")}
            {renderTraitGroup("primary", "Bottom")}
            {renderTraitGroup("success", "Switch")}

            <Stack spacing={3} sx={{ mt: 4 }}>
                {/* 구인 상태: 구인중 / 구인안함 */}
                <FormControl fullWidth>
                    <InputLabel>구인 상태</InputLabel>
                    <Select
                        value={recruitMain}
                        onChange={(e) => {
                            setRecruitMain(e.target.value);
                            setRecruitSub('');
                            setRecruitNote('');
                            setRecruitCustom('');
                        }}
                        label="구인 상태"
                    >
                        <MenuItem value="구인중">구인중</MenuItem>
                        <MenuItem value="구인안함">구인안함</MenuItem>
                    </Select>
                </FormControl>

                {/* 구인중 → 파트너 여부 */}
                {recruitMain === "구인중" && (
                    <FormControl fullWidth>
                        <InputLabel>파트너 여부</InputLabel>
                        <Select
                            value={recruitSub}
                            onChange={(e) => {
                                const v = e.target.value;
                                setRecruitSub(v);
                                if (v !== "기타") setRecruitCustom(""); // 기타 선택 해제 시 초기화
                            }}
                            label="파트너 여부"
                        >
                            <MenuItem value="파트너 있음">파트너 있음</MenuItem>
                            <MenuItem value="파트너 없음">파트너 없음</MenuItem>
                            <MenuItem value="기타">기타(직접작성)</MenuItem> {/* ✅ 여기 추가 */}
                        </Select>

                        {/* ✅ 기타 선택했을 때만 입력창 노출 */}
                        {recruitSub === "기타" && (
                            <TextField
                                label="직접 입력 (10자 이내)"
                                placeholder="예: 상황에 따라"
                                fullWidth
                                value={recruitCustom}
                                onChange={(e) => setRecruitCustom(e.target.value.slice(0, 10))}
                                inputProps={{ maxLength: 10 }}
                                helperText={`${recruitCustom.length}/10`}
                                sx={{ mt: 2 }}
                            />
                        )}
                    </FormControl>
                )}

                {/* 구인안함 → 세부 상태 선택 */}
                {recruitMain === "구인안함" && (
                    <>
                        <FormControl fullWidth>
                            <InputLabel>세부 상태</InputLabel>
                            <Select
                                value={recruitSub}
                                onChange={(e) => {
                                    setRecruitSub(e.target.value);
                                    setRecruitNote('');
                                }}
                                label="세부 상태"
                            >
                                <MenuItem value="생각없음">생각없음</MenuItem>
                                <MenuItem value="플파">플파</MenuItem>
                                <MenuItem value="디엣">디엣</MenuItem>
                                <MenuItem value="연디">연디</MenuItem>
                            </Select>
                        </FormControl>

                        {/* 이름 입력 (구인안함 제외) */}
                        {["플파", "디엣", "연디"].includes(recruitSub) && (
                            <TextField
                                label={`${recruitSub} 대상 이름 (선택)`}
                                value={recruitNote}
                                onChange={(e) => setRecruitNote(e.target.value)}
                                fullWidth
                            />
                        )}
                    </>
                )}

                {/* 플래그 입력 */}
                <TextField
                    label="관계 플래그 (예: 모노가미, 폴리)"
                    value={recruitFlag}
                    onChange={(e) => setRecruitFlag(e.target.value)}
                    fullWidth
                />

                {/* 오프 여부 */}
                <FormControl fullWidth>
                    <InputLabel>오프 모임</InputLabel>
                    <Select
                        value={offlineStatus}
                        onChange={(e) => setOfflineStatus(e.target.value)}
                        label="오프 모임"
                    >
                        <MenuItem value="오프(O)">오프(O)</MenuItem>
                        <MenuItem value="오프(X)">오프(X)</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
        </Box>
    );
}
